import { useTranslation } from "../../localization/useTranslation";
import { DotLegalSelectOption } from "../components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useCallback } from "react";

export const defaultCurrencyCode = "DKK";
const priorityNames = [defaultCurrencyCode, "EUR", "USD"];

export default function useCountryCurrencyHook() {
    const { translateCurrencyNames } = useTranslation();

    const getCurrenciesAsNames = useCallback((): Array<DotLegalSelectOption> => {
        const mappedCurrencies: Array<DotLegalSelectOption> = getIsoCodes().map((isoCode) => ({
            name: translateCurrencyNames(isoCode),
            id: isoCode,
        }));

        return sortCurrencies(mappedCurrencies, priorityNames);
    }, [translateCurrencyNames]);

    const getCurrenciesAsIsoCodes = useCallback((): Array<DotLegalSelectOption> => {
        const mappedCurrencies: Array<DotLegalSelectOption> = getIsoCodes().map((isoCode) => ({
            name: isoCode,
            id: isoCode,
        }));

        return sortCurrencies(mappedCurrencies, priorityNames);
    }, []); // Empty dependency array since ISO codes never change

    function sortCurrencies(currencies: Array<DotLegalSelectOption>, priorityNames: Array<string>): Array<DotLegalSelectOption> {
        return currencies.sort((a, b) => {
            const priorityA = priorityNames.indexOf(a.id);
            const priorityB = priorityNames.indexOf(b.id);

            if (priorityA !== -1 && priorityB !== -1) return priorityA - priorityB;
            if (priorityA !== -1) return -1;
            if (priorityB !== -1) return 1;

            return a.name.localeCompare(b.name);
        });
    }

    return { getCurrenciesAsNames, getCurrenciesAsIsoCodes };
}

function getIsoCodes() {
    return [
        "AED",
        "AFN",
        "ALL",
        "AMD",
        "ANG",
        "AOA",
        "ARS",
        "AUD",
        "AWG",
        "AZN",
        "BAM",
        "BBD",
        "BDT",
        "BGN",
        "BHD",
        "BIF",
        "BMD",
        "BND",
        "BOB",
        "BOV",
        "BRL",
        "BSD",
        "BTN",
        "BWP",
        "BYN",
        "BZD",
        "CAD",
        "CDF",
        "CHE",
        "CHF",
        "CHW",
        "CLF",
        "CLP",
        "CNY",
        "COP",
        "COU",
        "CRC",
        "CUC",
        "CUP",
        "CVE",
        "CZK",
        "DJF",
        "DKK",
        "DOP",
        "DZD",
        "EGP",
        "ERN",
        "ETB",
        "EUR",
        "FJD",
        "FKP",
        "GBP",
        "GEL",
        "GHS",
        "GIP",
        "GMD",
        "GNF",
        "GTQ",
        "GYD",
        "HKD",
        "HNL",
        "HRK",
        "HTG",
        "HUF",
        "IDR",
        "ILS",
        "INR",
        "IQD",
        "IRR",
        "ISK",
        "JMD",
        "JOD",
        "JPY",
        "KES",
        "KGS",
        "KHR",
        "KMF",
        "KPW",
        "KRW",
        "KWD",
        "KYD",
        "KZT",
        "LAK",
        "LBP",
        "LKR",
        "LRD",
        "LSL",
        "LYD",
        "MAD",
        "MDL",
        "MGA",
        "MKD",
        "MMK",
        "MNT",
        "MOP",
        "MRU",
        "MUR",
        "MVR",
        "MWK",
        "MXN",
        "MYR",
        "MZN",
        "NAD",
        "NGN",
        "NIO",
        "NOK",
        "NPR",
        "NZD",
        "OMR",
        "PAB",
        "PEN",
        "PGK",
        "PHP",
        "PKR",
        "PLN",
        "PYG",
        "QAR",
        "RON",
        "RSD",
        "RUB",
        "RWF",
        "SAR",
        "SBD",
        "SCR",
        "SDG",
        "SEK",
        "SGD",
        "SHP",
        "SLL",
        "SOS",
        "SRD",
        "SSP",
        "STN",
        "SYP",
        "SZL",
        "THB",
        "TJS",
        "TMT",
        "TND",
        "TOP",
        "TRY",
        "TTD",
        "TWD",
        "TZS",
        "UAH",
        "UGX",
        "USD",
        "UYU",
        "UZS",
        "VES",
        "VND",
        "XAF",
        "XCD",
        "XOF",
        "XPF",
        "ZAR",
        "ZMW",
        "ZWL",
    ];
}
